import React, { useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { Spy } from '@styled-icons/remix-fill';
import { User as UserRole } from 'lib/User';
import dayjs from 'dayjs';
import styled, { keyframes } from 'styled-components';

import { Loading } from 'components/Loading';

import { throwMessage } from 'utils/LoggingService';

import { TotemLoadingStyle } from 'styles/pages/totem.styles';
import {
    SettingsTitleContainerWrapper,
    SettingsTitleContainer,
    SettingsTitle,
    SettingsPageStyle,
    SettingsCard,
} from 'styles/pages/settings.styles';
import { SPYEE_TOKEN, SPYEE_REFRESH_TOKEN } from 'constants/index';
import { TotemInput } from 'styles';
import { GetAppUsersQuery, useGetAppUsersQuery, useFetchUserTokenMutation } from 'data/__generated__';

// interface AppUser {
//     _id: string;
//     username: string;
//     organization: { name: string };
//     lastSeenAt?: string;
// }

// interface GetAppUsersData {
//     appUsers: AppUser[];
// }

export const Spying: React.FC = () => {
    const client = useApolloClient();
    const [inputValue, setInputValue] = useState<string>('');
    const { loading, error, data } = useGetAppUsersQuery({
        fetchPolicy: 'no-cache',
    });
    const [fetchUserToken] = useFetchUserTokenMutation();
    let users: GetAppUsersQuery['appUsers'] = [];

    if (loading) {
        return (
            <TotemLoadingStyle>
                <Loading />
            </TotemLoadingStyle>
        );
    }

    if (error) {
        throwMessage('warn', error.message);
    }

    if (data && data.appUsers.length) {
        users = data.appUsers.sort((a, b) => (a?.lastSeenAt && b?.lastSeenAt && a.lastSeenAt > b.lastSeenAt ? -1 : 1));
    }

    const handleUserSelection = async (userId: string) => {
        const userToken = await fetchUserToken({ variables: { spyeeId: userId } });
        const result = userToken?.data?.fetchSpyeeToken;

        if (result?.success && result.token) {
            window.TOTEM.storage.set(SPYEE_TOKEN, result.token);
            if (result.refreshToken) {
                window.TOTEM.storage.set(SPYEE_REFRESH_TOKEN, result.refreshToken);
            }
            client.onResetStore(async () => {
                if (window.TOTEM.storage.get(SPYEE_TOKEN)) {
                    window.location.replace('/');
                }
                return Promise.resolve();
            });
            client.resetStore();
        }
    };

    const filteredUsers = users.filter(
        (user) =>
            user?._id !== UserRole.get()._id &&
            (!inputValue ||
                user?.organization?.name
                    .replace(/ /g, '')
                    .toLowerCase()
                    .includes(inputValue.replace(/ /g, '').toLowerCase()) ||
                user?.username.replace(/ /g, '').toLowerCase().includes(inputValue.replace(/ /g, '').toLowerCase())),
    );

    return (
        <SettingsPageStyle>
            <SettingsCard>
                <SettingsTitleContainerWrapper>
                    <SettingsTitleContainer>
                        <Spy />
                        <SettingsSpecialTitle>Espionnage de clients</SettingsSpecialTitle>
                    </SettingsTitleContainer>
                </SettingsTitleContainerWrapper>
                <TotemInput
                    autoFocus
                    placeholder="Cherchez une organisation à espionner"
                    onChange={({ target: { value } }) => setInputValue(value)}
                />
                {filteredUsers.length ? (
                    <ResultsContainer>
                        {filteredUsers?.map((user) => {
                            if (!user) return null;
                            const { _id, username, organization, lastSeenAt } = user;
                            return (
                                <UserOption key={_id} onClick={() => handleUserSelection(_id)}>
                                    <UserCompanyStyle>
                                        {organization?.name}
                                        <UserEmailStyle>{username}</UserEmailStyle>
                                    </UserCompanyStyle>
                                    <div>{`Actif ${lastSeenAt ? dayjs(lastSeenAt).fromNow() : 'Never'}`}</div>
                                </UserOption>
                            );
                        })}
                    </ResultsContainer>
                ) : (
                    <div>Aucun résultat... Vous êtes sûr·e que ça s'écrit comme ça ?</div>
                )}
            </SettingsCard>
        </SettingsPageStyle>
    );
};

const colorRotate = keyframes`
    to {
    background-position: 200% center;
  }
`;

const SettingsSpecialTitle = styled(SettingsTitle)`
    background-image: linear-gradient(-225deg, #231557 0%, #44107a 29%, #ff1361 67%, #fff800 100%);
    background-size: auto auto;
    background-clip: border-box;
    background-size: 200% auto;
    color: #ffffff;
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: textclip 2s linear infinite;
    display: inline-block;
    animation: ${colorRotate} 3s linear 0s infinite;
`;

const ResultsContainer = styled.div`
    position: relative;
    flex-direction: column;
    justify-content: flex-start;
    width: 100%;
    border-radius: ${(props) => props.theme.variables.borderRadius};
    border: 1px solid ${(props) => props.theme.colors.borderGrey};
`;

const UserOption = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: ${(props) => props.theme.variables.margin}px;
    &:hover {
        background: ${(props) => props.theme.colors.whiteGrey};
        cursor: pointer;
        color: ${(props) => props.theme.colors.black};
    }
    border-bottom: 1px solid ${(props) => props.theme.colors.borderGrey};
    background-color: #ffffff;
    border-radius: ${(props) => props.theme.variables.borderRadius};
`;

const UserCompanyStyle = styled.div`
    color: ${(props) => props.theme.variables.secondary};
    font-size: 20px;
    font-weight: 500;
`;

const UserEmailStyle = styled.div`
    font-size: 12px;
    color: ${(props) => props.theme.colors.pantinGrey};
`;
